/**
 * Bluetooth Thermal Printer Service
 * Uses Web Bluetooth API to connect to ESC/POS compatible printers
 */

// ESC/POS Commands
const ESC = 0x1B;
const GS = 0x1D;
const COMMANDS = {
  INIT: [ESC, 0x40], // Initialize printer
  ALIGN_LEFT: [ESC, 0x61, 0x00],
  ALIGN_CENTER: [ESC, 0x61, 0x01],
  ALIGN_RIGHT: [ESC, 0x61, 0x02],
  BOLD_ON: [ESC, 0x45, 0x01],
  BOLD_OFF: [ESC, 0x45, 0x00],
  DOUBLE_HEIGHT_ON: [GS, 0x21, 0x10],
  DOUBLE_WIDTH_ON: [GS, 0x21, 0x20],
  DOUBLE_ON: [GS, 0x21, 0x30],
  NORMAL_SIZE: [GS, 0x21, 0x00],
  UNDERLINE_ON: [ESC, 0x2D, 0x01],
  UNDERLINE_OFF: [ESC, 0x2D, 0x00],
  CUT_PAPER: [GS, 0x56, 0x00], // Full cut
  CUT_PAPER_PARTIAL: [GS, 0x56, 0x01], // Partial cut
  FEED_LINE: [0x0A], // Line feed
  FEED_LINES: (n) => [ESC, 0x64, n], // Feed n lines
};

class BluetoothPrinterService {
  constructor() {
    this.device = null;
    this.server = null;
    this.characteristic = null;
    this.isConnected = false;
    this.printerName = null;
  }

  /**
   * Check if Web Bluetooth is supported
   */
  isSupported() {
    return 'bluetooth' in navigator;
  }

  /**
   * Request and connect to a Bluetooth printer
   */
  async connect() {
    if (!this.isSupported()) {
      throw new Error('Web Bluetooth is not supported in this browser');
    }

    try {
      // Request device with common thermal printer services
      this.device = await navigator.bluetooth.requestDevice({
        acceptAllDevices: true,
        optionalServices: [
          '000018f0-0000-1000-8000-00805f9b34fb', // Common thermal printer service
          '49535343-fe7d-4ae5-8fa9-9fafd205e455', // Another common service
          'e7810a71-73ae-499d-8c15-faa9aef0c3f2', // Serial port service
        ]
      });

      this.printerName = this.device.name || 'Unknown Printer';
      
      // Connect to GATT server
      this.server = await this.device.gatt.connect();
      
      // Try to find the characteristic for writing
      const services = await this.server.getPrimaryServices();
      
      for (const service of services) {
        const characteristics = await service.getCharacteristics();
        for (const char of characteristics) {
          if (char.properties.write || char.properties.writeWithoutResponse) {
            this.characteristic = char;
            break;
          }
        }
        if (this.characteristic) break;
      }

      if (!this.characteristic) {
        throw new Error('No writable characteristic found on printer');
      }

      this.isConnected = true;
      
      // Listen for disconnection
      this.device.addEventListener('gattserverdisconnected', () => {
        this.isConnected = false;
        this.characteristic = null;
        this.server = null;
      });

      return {
        success: true,
        printerName: this.printerName
      };
    } catch (error) {
      this.isConnected = false;
      throw error;
    }
  }

  /**
   * Disconnect from printer
   */
  disconnect() {
    if (this.device && this.device.gatt.connected) {
      this.device.gatt.disconnect();
    }
    this.isConnected = false;
    this.characteristic = null;
    this.server = null;
    this.device = null;
  }

  /**
   * Write raw bytes to printer
   */
  async writeRaw(data) {
    if (!this.isConnected || !this.characteristic) {
      throw new Error('Printer not connected');
    }

    const uint8Array = new Uint8Array(data);
    
    // Split into chunks if needed (BLE has MTU limits)
    const chunkSize = 100;
    for (let i = 0; i < uint8Array.length; i += chunkSize) {
      const chunk = uint8Array.slice(i, i + chunkSize);
      if (this.characteristic.properties.writeWithoutResponse) {
        await this.characteristic.writeValueWithoutResponse(chunk);
      } else {
        await this.characteristic.writeValue(chunk);
      }
      // Small delay between chunks
      await new Promise(resolve => setTimeout(resolve, 50));
    }
  }

  /**
   * Write text to printer
   */
  async writeText(text) {
    const encoder = new TextEncoder();
    const data = encoder.encode(text);
    await this.writeRaw([...data]);
  }

  /**
   * Print a formatted receipt
   */
  async printReceipt(receiptData) {
    if (!this.isConnected) {
      throw new Error('Printer not connected');
    }

    const commands = [];
    
    // Initialize printer
    commands.push(...COMMANDS.INIT);
    
    // Header - Business name (centered, bold, double size)
    commands.push(...COMMANDS.ALIGN_CENTER);
    commands.push(...COMMANDS.DOUBLE_ON);
    commands.push(...COMMANDS.BOLD_ON);
    commands.push(...this.textToBytes(receiptData.businessName || 'SAVANNA POS'));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...COMMANDS.NORMAL_SIZE);
    commands.push(...COMMANDS.BOLD_OFF);
    
    // Address and phone
    if (receiptData.address) {
      commands.push(...this.textToBytes(receiptData.address));
      commands.push(...COMMANDS.FEED_LINE);
    }
    if (receiptData.phone) {
      commands.push(...this.textToBytes(`Tel: ${receiptData.phone}`));
      commands.push(...COMMANDS.FEED_LINE);
    }
    
    // Divider
    commands.push(...this.textToBytes('--------------------------------'));
    commands.push(...COMMANDS.FEED_LINE);
    
    // Receipt info (left aligned)
    commands.push(...COMMANDS.ALIGN_LEFT);
    commands.push(...this.textToBytes(`Receipt: ${receiptData.receiptNumber || ''}`));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...this.textToBytes(`Date: ${receiptData.date || new Date().toLocaleString()}`));
    commands.push(...COMMANDS.FEED_LINE);
    if (receiptData.tableNumber) {
      commands.push(...this.textToBytes(`Table: ${receiptData.tableNumber}`));
      commands.push(...COMMANDS.FEED_LINE);
    }
    if (receiptData.customerName && receiptData.customerName !== 'Walk-in') {
      commands.push(...this.textToBytes(`Customer: ${receiptData.customerName}`));
      commands.push(...COMMANDS.FEED_LINE);
    }
    if (receiptData.servedBy) {
      commands.push(...this.textToBytes(`Served by: ${receiptData.servedBy}`));
      commands.push(...COMMANDS.FEED_LINE);
    }
    
    // Divider
    commands.push(...this.textToBytes('--------------------------------'));
    commands.push(...COMMANDS.FEED_LINE);
    
    // Items header
    commands.push(...COMMANDS.BOLD_ON);
    commands.push(...this.textToBytes(this.formatLine('Item', 'Qty', 'Amount')));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...COMMANDS.BOLD_OFF);
    commands.push(...this.textToBytes('--------------------------------'));
    commands.push(...COMMANDS.FEED_LINE);
    
    // Items
    for (const item of (receiptData.items || [])) {
      const name = item.name.length > 16 ? item.name.substring(0, 16) : item.name;
      const qty = item.quantity.toString();
      const amount = this.formatCurrency(item.price * item.quantity);
      commands.push(...this.textToBytes(this.formatLine(name, qty, amount)));
      commands.push(...COMMANDS.FEED_LINE);
    }
    
    // Divider
    commands.push(...this.textToBytes('--------------------------------'));
    commands.push(...COMMANDS.FEED_LINE);
    
    // Totals
    commands.push(...this.textToBytes(this.formatTotal('Subtotal:', this.formatCurrency(receiptData.subtotal || 0))));
    commands.push(...COMMANDS.FEED_LINE);
    
    // Tax breakdown
    if (receiptData.taxes) {
      for (const [taxName, taxInfo] of Object.entries(receiptData.taxes)) {
        commands.push(...this.textToBytes(this.formatTotal(`${taxName} (${taxInfo.rate}%):`, this.formatCurrency(taxInfo.amount))));
        commands.push(...COMMANDS.FEED_LINE);
      }
    } else if (receiptData.tax) {
      commands.push(...this.textToBytes(this.formatTotal('Tax:', this.formatCurrency(receiptData.tax))));
      commands.push(...COMMANDS.FEED_LINE);
    }
    
    // Discount
    if (receiptData.discount && receiptData.discount > 0) {
      commands.push(...this.textToBytes(this.formatTotal('Discount:', `-${this.formatCurrency(receiptData.discount)}`)));
      commands.push(...COMMANDS.FEED_LINE);
    }
    
    // Total (bold, larger)
    commands.push(...COMMANDS.BOLD_ON);
    commands.push(...COMMANDS.DOUBLE_HEIGHT_ON);
    commands.push(...this.textToBytes(this.formatTotal('TOTAL:', this.formatCurrency(receiptData.total || 0))));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...COMMANDS.NORMAL_SIZE);
    commands.push(...COMMANDS.BOLD_OFF);
    
    // Payment method
    commands.push(...this.textToBytes('--------------------------------'));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...this.textToBytes(`Payment: ${receiptData.paymentMethod || 'Cash'}`));
    commands.push(...COMMANDS.FEED_LINE);
    
    if (receiptData.mpesaReceipt) {
      commands.push(...this.textToBytes(`M-Pesa: ${receiptData.mpesaReceipt}`));
      commands.push(...COMMANDS.FEED_LINE);
    }
    
    // Loyalty points
    if (receiptData.pointsEarned && receiptData.pointsEarned > 0) {
      commands.push(...COMMANDS.FEED_LINE);
      commands.push(...COMMANDS.ALIGN_CENTER);
      commands.push(...this.textToBytes(`+${receiptData.pointsEarned} loyalty points earned!`));
      commands.push(...COMMANDS.FEED_LINE);
    }
    
    // Footer
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...COMMANDS.ALIGN_CENTER);
    commands.push(...this.textToBytes(receiptData.footer || 'Thank you for your visit!'));
    commands.push(...COMMANDS.FEED_LINE);
    commands.push(...this.textToBytes('Please come again'));
    commands.push(...COMMANDS.FEED_LINE);
    
    // Reprint notice
    if (receiptData.isReprint) {
      commands.push(...COMMANDS.FEED_LINE);
      commands.push(...COMMANDS.BOLD_ON);
      commands.push(...this.textToBytes('*** REPRINT ***'));
      commands.push(...COMMANDS.BOLD_OFF);
      commands.push(...COMMANDS.FEED_LINE);
    }
    
    // Feed and cut
    commands.push(...COMMANDS.FEED_LINES(4));
    commands.push(...COMMANDS.CUT_PAPER_PARTIAL);
    
    // Send to printer
    await this.writeRaw(commands);
    
    return { success: true };
  }

  // Helper: Convert text to bytes
  textToBytes(text) {
    const encoder = new TextEncoder();
    return [...encoder.encode(text)];
  }

  // Helper: Format currency
  formatCurrency(amount) {
    return `KSh ${(amount || 0).toLocaleString('en-KE', { minimumFractionDigits: 2 })}`;
  }

  // Helper: Format line with columns (32 char width for 58mm paper)
  formatLine(name, qty, amount) {
    const width = 32;
    const qtyWidth = 3;
    const amountWidth = 10;
    const nameWidth = width - qtyWidth - amountWidth - 2;
    
    const namePadded = name.padEnd(nameWidth).substring(0, nameWidth);
    const qtyPadded = qty.padStart(qtyWidth);
    const amountPadded = amount.padStart(amountWidth);
    
    return `${namePadded} ${qtyPadded} ${amountPadded}`;
  }

  // Helper: Format total line
  formatTotal(label, amount) {
    const width = 32;
    const space = width - label.length - amount.length;
    return `${label}${' '.repeat(Math.max(1, space))}${amount}`;
  }
}

// Export singleton instance
export const bluetoothPrinter = new BluetoothPrinterService();
export default bluetoothPrinter;
